import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  ExternalLink,
  RefreshCw,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Truck,
  Wrench,
  Calendar,
  Clock,
  Phone,
  MapPin,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  Link,
  Check,
  X,
  Search,
  User,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useState, useMemo } from 'react';

import clsx from 'clsx';
import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

/**
 * Get slot label and formatted time for a job.
 * - If slot_label exists, use it directly.
 * - If scheduled_time is "Morning"/"Afternoon", use that as slot.
 * - Otherwise infer from the time (before 1pm = Morning, after = Afternoon).
 */
/**
 * Which visit of the contract this is: "3 of 6".
 *
 * Taken from the job title, which is what Zuper's recurrence template stamps on
 * each job and what the contract means by "visit 3 of 6".
 *
 * NOT from the recurrence link (v_job_series.visit_no / visit_count). Those
 * count the jobs that happen to exist in jms under one recurring_job_uid, which
 * is a different number and usually a smaller one: WO 53041 reads "visit 1 of
 * 4" there because visits 1 and 2 were created under an earlier series, and
 * WO 28813 reads "4 of 5" because visit 3 is missing from its series. Measured
 * on a sample of 40, the two disagree half the time, and the title is the one
 * that matches the contract. Title coverage is 911 of 941 upcoming TBC jobs;
 * the recurrence link covers 727.
 *
 * Handles every spelling in use: "AMC Visit 3 of 6", "AMC Silver 3 of 6",
 * "AMC Visit: 5 of 6", "AMC Platinum Visit 4 of 12".
 */
function visitOf(job) {
  const m = /(\d+)\s*of\s*(\d+)/i.exec(job?.job_title || '');
  if (!m) return null;
  const visit = Number(m[1]);
  const total = Number(m[2]);
  if (!visit || !total || visit > total) return null;
  return { visit, total, label: `${visit} of ${total}`, remaining: total - visit };
}

function getScheduleDisplay(job) {
  const time = job.scheduled_time;
  let slot = job.slot_label || null;
  let displayTime = time;

  if (time === 'Morning' || time === 'Afternoon') {
    slot = slot || time;
    displayTime = null; // no actual time to show
  } else if (time && !slot) {
    // Infer slot from time string like "1:00 PM", "10:00 AM"
    const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const period = match[3].toUpperCase();
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      slot = hours >= 13 ? 'Afternoon' : 'Morning';
    }
  }

  return { slot, displayTime };
}

export default function Dashboard() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [sortField, setSortField] = useState(() => localStorage.getItem('dashboard_sortField') || 'schedule');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('dashboard_sortDir') || 'asc');
  const [statusFilter, setStatusFilter] = useState('tbc');
  const [toast, setToast] = useState(null);
  const [drawerJob, setDrawerJob] = useState(null);
  const queryClient = useQueryClient();
  const { selectedLocationId, currentLocation } = useLocationContext();

  // Single query: fetch live from Zuper
  const { data: liveData, isLoading: jobsInitialLoading, isFetching: jobsLoading, refetch } = useQuery({
    queryKey: ['live-jobs', weekOffset, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/live-jobs?weekOffset=${weekOffset}`);
      return data;
    },
  });

  const stats = liveData ? {
    tbc_jobs: liveData.counts?.tbc || 0,
    scheduled_jobs: liveData.counts?.scheduled || 0,
    total_jobs: liveData.counts?.total || 0,
    date_range: liveData.dateRange,
    zuperAppUrl: '',
  } : null;
  const [jobSearch, setJobSearch] = useState('');
  const availability = liveData?.availability || [];
  const tbcData = liveData ? { jobs: liveData.tbcJobs, dateRange: liveData.dateRange } : null;
  const scheduledData = liveData ? { jobs: liveData.scheduledJobs, dateRange: liveData.dateRange } : null;

  // Fetch job details from Zuper when drawer is open
  const { data: jobDetails, isLoading: jobDetailsLoading } = useQuery({
    queryKey: ['job-details', drawerJob?.job_uid],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/job-details/${drawerJob.job_uid}`);
      return data;
    },
    enabled: !!drawerJob?.job_uid,
  });

  const zuperAppUrl = 'https://web.zuperpro.com';

  const openJobInZuper = (e, jobUid) => {
    e.stopPropagation();
    if (!jobUid) return;
    window.open(`${zuperAppUrl}/jobs/${jobUid}/details`, '_blank');
  };

  const copyBookingLink = (customerUid, jobUid) => {
    const url = `${window.location.origin}/book/${customerUid}/${jobUid}`;
    navigator.clipboard.writeText(url);
    setToast('Booking link copied!');
    setTimeout(() => setToast(null), 2000);
  };

  const goToPrevWeek = () => setWeekOffset(w => w - 1);
  const goToNextWeek = () => setWeekOffset(w => w + 1);
  const goToCurrentWeek = () => setWeekOffset(0);

  const handleSort = (field) => {
    if (sortField === field) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      localStorage.setItem('dashboard_sortDir', newDir);
    } else {
      setSortField(field);
      setSortDir('asc');
      localStorage.setItem('dashboard_sortField', field);
      localStorage.setItem('dashboard_sortDir', 'asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const tbcJobs = useMemo(() => {
    const source = statusFilter === 'tbc' ? tbcData : scheduledData;
    let jobs = [...(source?.jobs || [])];
    // Search what someone would actually have in front of them: a job number
    // from Zuper, a customer on the phone, a buggy or its serial. Phone matches
    // on digits too, so "50 702 1971" or "7021971" both find
    // "(971)(50)702-1971" — punctuation and spacing in either the query or the
    // stored number stop mattering.
    const q = jobSearch.trim().toLowerCase();
    if (q) {
      const digits = q.replace(/\D/g, '');
      jobs = jobs.filter((j) => {
        const haystack = [
          j.work_order_number, j.customer_name, j.asset_name, j.asset_serial_number,
          j.asset_code, j.job_title, j.job_category, j.workshop_location,
          j.assigned_mechanic_name, j.customer_phone,
        ].filter(Boolean).join(' ').toLowerCase();
        if (haystack.includes(q)) return true;
        return digits.length >= 3 && String(j.customer_phone || '').replace(/\D/g, '').includes(digits);
      });
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return jobs.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'workorder':
          cmp = (Number(a.work_order_number) || 0) - (Number(b.work_order_number) || 0);
          break;
        case 'customer':
          cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
          break;
        case 'asset':
          cmp = (a.asset_name || '').localeCompare(b.asset_name || '');
          break;
        case 'category':
          cmp = (a.job_category || '').localeCompare(b.job_category || '');
          break;
        case 'status': {
          // The column shows the visit now, and the list is filtered to a
          // single status, so sorting on the status string never moved a row.
          // Sorts by how far through the contract a job is; jobs whose title
          // carries no number sort last in either direction.
          const va = visitOf(a), vb = visitOf(b);
          // `cmp * dir` is applied below, and dir * dir === 1, so returning
          // `dir` here keeps an unnumbered job last in BOTH directions rather
          // than flipping it to the top when sorting descending.
          if (!va && !vb) cmp = 0;
          else if (!va) cmp = dir;
          else if (!vb) cmp = -dir;
          else cmp = (va.visit - vb.visit) || (va.total - vb.total);
          break;
        }
        case 'schedule':
          cmp = (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
          if (cmp === 0) cmp = (a.scheduled_time || '').localeCompare(b.scheduled_time || '');
          break;
        case 'phone':
          cmp = (a.customer_phone || '').localeCompare(b.customer_phone || '');
          break;
        default:
          cmp = 0;
      }
      return cmp * dir;
    });
  }, [tbcData?.jobs, scheduledData?.jobs, statusFilter, sortField, sortDir, jobSearch]);

  const dateRange = tbcData?.dateRange || stats?.date_range || '';

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'Current Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset > 0) return `${weekOffset} Weeks Ahead`;
    return `${Math.abs(weekOffset)} Weeks Ago`;
  };

  const getJobColor = (category) => {
    const colors = {
      'AMC - Workshop': 'bg-brand-dark',
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryIcon = (category) => {
    if (category?.includes('Workshop')) return Wrench;
    return Truck;
  };

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={jobsLoading} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <LayoutDashboard className="w-5 h-5 md:w-6 md:h-6 text-brand-primary" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Service Dashboard</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Weekly dispatch board and job overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={jobsLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light/80 hover:border-brand-neutral/30 transition-all shadow-sm disabled:opacity-50"
            title="Refresh from Zuper"
          >
            <RefreshCw className={`w-4 h-4 ${jobsLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{jobsLoading ? 'Loading...' : 'Sync'}</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-full mx-auto space-y-3 md:space-y-4">
          {/* Live data info */}

          {/* Week Navigation */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light p-3 md:p-4">
            {/* Mobile: Stacked Layout */}
            <div className="flex flex-col gap-3 md:hidden">
              {/* Week Selector */}
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPrevWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="text-center flex-1">
                  <h2 className="text-base font-bold text-brand-dark">{getWeekLabel()}</h2>
                  <p className="text-xs text-brand-neutral">{dateRange}</p>
                </div>

                <button
                  onClick={goToNextWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {weekOffset !== 0 && (
                <button
                  onClick={goToCurrentWeek}
                  className="w-full px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
                >
                  Back to Current Week
                </button>
              )}

              {/* Stats Row */}
              <div className="flex gap-3">
                <button onClick={() => setStatusFilter('tbc')} className={`flex-1 text-center px-3 py-3 rounded-xl border transition-all ${statusFilter === 'tbc' ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-300 ring-2 ring-amber-200' : 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-100 opacity-60'}`}>
                  <p className="text-2xl font-bold text-amber-600">{jobsLoading ? '-' : stats?.tbc_jobs ?? '-'}</p>
                  <p className="text-[10px] font-medium text-amber-700/70 uppercase tracking-wider">TBC Jobs</p>
                </button>
                <button onClick={() => setStatusFilter('scheduled')} className={`flex-1 text-center px-3 py-3 rounded-xl border transition-all ${statusFilter === 'scheduled' ? 'bg-gradient-to-br from-brand-light to-green-50 border-brand-primary ring-2 ring-green-200' : 'bg-gradient-to-br from-brand-light to-green-50 border-brand-light opacity-60'}`}>
                  <p className="text-2xl font-bold text-brand-primary">{jobsLoading ? '-' : stats?.scheduled_jobs ?? '-'}</p>
                  <p className="text-[10px] font-medium text-brand-neutral uppercase tracking-wider">Scheduled</p>
                </button>
              </div>
            </div>

            {/* Desktop: Horizontal Layout */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPrevWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                  title="Previous week"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="text-center min-w-[200px]">
                  <h2 className="text-lg font-bold text-brand-dark">{getWeekLabel()}</h2>
                  <p className="text-sm text-brand-neutral">{dateRange}</p>
                </div>

                <button
                  onClick={goToNextWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                  title="Next week"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {weekOffset !== 0 && (
                  <button
                    onClick={goToCurrentWeek}
                    className="ml-2 px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
                  >
                    Today
                  </button>
                )}
              </div>

              <div className="flex items-center gap-4">
                <button onClick={() => setStatusFilter('tbc')} className={`text-center px-5 py-3 rounded-xl border cursor-pointer transition-all ${statusFilter === 'tbc' ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-300 ring-2 ring-amber-200 shadow-md' : 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-100 opacity-60 hover:opacity-80'}`}>
                  <p className="text-2xl font-bold text-amber-600">{jobsLoading ? '-' : stats?.tbc_jobs ?? '-'}</p>
                  <p className="text-[10px] font-medium text-amber-700/70 uppercase tracking-wider">TBC Jobs</p>
                </button>
                <button onClick={() => setStatusFilter('scheduled')} className={`text-center px-5 py-3 rounded-xl border cursor-pointer transition-all ${statusFilter === 'scheduled' ? 'bg-gradient-to-br from-brand-light to-green-50 border-brand-primary ring-2 ring-green-200 shadow-md' : 'bg-gradient-to-br from-brand-light to-green-50 border-brand-light opacity-60 hover:opacity-80'}`}>
                  <p className="text-2xl font-bold text-brand-primary">{jobsLoading ? '-' : stats?.scheduled_jobs ?? '-'}</p>
                  <p className="text-[10px] font-medium text-brand-neutral uppercase tracking-wider">Total Scheduled</p>
                </button>
              </div>
            </div>

            {/* Category Legend - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-4 mt-4 pt-4 border-t border-brand-light">
              <span className="text-xs text-brand-neutral font-medium">Category:</span>
              <div className="flex gap-4">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-brand-dark"></span>
                  <Wrench className="w-3 h-3 text-brand-dark" />
                  <span className="text-xs text-brand-dark">Workshop</span>
                </span>
              </div>
            </div>
          </div>

          {/* What can actually still be booked.
              A TBC job below has a planned date from Zuper's recurrence — it is
              not a reservation and holds no slot. This strip is the real answer
              to "can I fit another one in?", computed the same way the booking
              page computes it, so the two screens agree. */}
          {availability.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden mb-4">
              <div className="px-4 md:px-6 py-3 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white flex items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-brand-dark">Slots still bookable</h3>
                <span className="text-[11px] text-brand-neutral">
                  Needs a free AMC tech plus collection, washing &amp; delivery
                </span>
              </div>
              <div className="p-3 md:p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {availability.map((day) => {
                  const dayFull = day.slots.every((s) => s.remaining === 0);
                  return (
                    <div
                      key={day.date}
                      className={clsx(
                        'rounded-xl border p-2.5',
                        dayFull ? 'border-red-100 bg-red-50/40' : 'border-brand-light bg-white',
                      )}
                    >
                      <p className="text-[11px] font-semibold text-brand-dark">
                        {new Date(day.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <div className="mt-1.5 space-y-1">
                        {day.slots.map((s) => (
                          <div key={s.label} className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-brand-neutral">{s.label}</span>
                            <span
                              className={clsx(
                                'text-[11px] font-bold tabular-nums',
                                s.remaining === 0 ? 'text-red-500' : s.remaining <= 1 ? 'text-amber-600' : 'text-brand-primary',
                              )}
                            >
                              {s.remaining === 0 ? 'Full' : `${s.remaining} free`}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="mt-1.5 text-[10px] text-brand-neutral/70">
                        {day.slots.reduce((a, s) => a + s.booked, 0)} booked
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Jobs List */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${statusFilter === 'tbc' ? 'bg-amber-400 animate-pulse' : 'bg-brand-primary'}`} />
                {statusFilter === 'tbc' ? 'TBC Jobs' : 'Scheduled Jobs'}{' '}
                {!jobsLoading && (
                  <span className="text-brand-neutral font-normal">
                    ({tbcJobs.length}{jobSearch.trim() ? ` of ${(statusFilter === 'tbc' ? tbcData : scheduledData)?.jobs?.length || 0}` : ''})
                  </span>
                )}
              </h3>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-brand-neutral/50 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  placeholder="Search job #, customer, buggy, phone…"
                  className="w-full pl-9 pr-8 py-1.5 text-xs rounded-lg border border-brand-light bg-white text-brand-dark placeholder:text-brand-neutral/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
                {jobSearch && (
                  <button
                    onClick={() => setJobSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-neutral/50 hover:text-brand-dark"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {jobsLoading ? (
              <div className="p-8 md:p-12 text-center text-brand-neutral">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-3 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              </div>
            ) : tbcJobs.length === 0 ? (
              <div className="p-8 md:p-12 text-center text-brand-neutral">
                <p className="text-sm">No TBC jobs for this week.</p>
                <button
                  onClick={() => refetch()}
                  disabled={jobsLoading}
                  className="mt-4 px-4 py-2 text-sm font-medium text-brand-primary bg-brand-light rounded-xl hover:bg-brand-primary hover:text-white transition-colors"
                >
                  Sync from Zuper
                </button>
              </div>
            ) : (
              <>
                {/* Mobile: Card Layout */}
                <div className="md:hidden divide-y divide-brand-light/50">
                  {tbcJobs.map((job) => {
                    const CategoryIcon = getCategoryIcon(job.job_category);

                    return (
                      <div key={job.job_uid} className="p-4 space-y-3 border-l-3 border-transparent hover:border-brand-primary/30 transition-colors">
                        {/* Header Row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium text-white ${getJobColor(job.job_category)}`}>
                                <CategoryIcon className="w-3 h-3" />
                                {job.job_category || 'N/A'}
                              </span>
                              {visitOf(job) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-brand-light text-brand-primary">
                                  {visitOf(job).label}
                                </span>
                              )}
                            </div>
                            <p className="font-medium text-brand-dark truncate">
                              {job.customer_name || 'Unknown'}
                            </p>
                            {job.job_title && (
                              <p className="text-xs text-brand-neutral/80 truncate">{job.job_title}</p>
                            )}
                            {job.asset_name && (
                              <p className="text-xs text-brand-neutral truncate">
                                {job.asset_code ? `${job.asset_code} - ` : ''}{job.asset_name}{job.asset_serial_number ? ` · ${job.asset_serial_number}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 group/wo">
                            <span className="text-[10px] font-mono text-brand-neutral bg-brand-light px-2 py-1 rounded">
                              #{job.work_order_number || 'N/A'}
                            </span>
                            <button
                              onClick={(e) => openJobInZuper(e, job.job_uid)}
                              className="p-1 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                              title="Open job in Zuper"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                            {job.work_order_number && (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(String(job.work_order_number)); setToast('Job number copied!'); setTimeout(() => setToast(null), 2000); }}
                                className="p-1 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                                title="Copy job number"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Info Row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-neutral">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {job.workshop_location || 'No Location'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {job.scheduled_date || 'Not scheduled'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {(() => {
                              const { slot, displayTime } = getScheduleDisplay(job);
                              return [displayTime, slot].filter(Boolean).join(' · ') || '-';
                            })()}
                          </span>
                          {job.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {job.customer_phone}
                            </span>
                          )}
                        </div>

                        {/* Action */}
                        {statusFilter === 'tbc' ? (
                          <button
                            onClick={() => copyBookingLink(job.customer_uid, job.job_uid)}
                            className="w-full flex items-center justify-center gap-2 text-brand-primary font-medium px-3 py-2 rounded-xl bg-brand-light hover:bg-brand-primary hover:text-white transition-colors text-sm"
                          >
                            <Link className="w-4 h-4" /> Copy Booking Link
                          </button>
                        ) : (
                          <button
                            onClick={() => setDrawerJob(job)}
                            className="w-full flex items-center justify-center gap-2 text-brand-primary font-medium px-3 py-2 rounded-xl bg-brand-light hover:bg-brand-primary hover:text-white transition-colors text-sm"
                          >
                            <Wrench className="w-4 h-4" /> Job Details
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: Table Layout */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gradient-to-r from-brand-light/50 to-brand-light/20 text-brand-dark font-semibold border-b border-brand-light/60">
                      <tr>
                        {[
                          { field: 'workorder', label: 'Job #' },
                          { field: 'customer', label: 'Customer' },
                          { field: 'asset', label: 'Asset' },
                          { field: 'category', label: 'Category' },
                          { field: 'status', label: 'AMC Visit' },
                          { field: 'schedule', label: 'Schedule' },
                          { field: 'phone', label: 'Phone' },
                        ].map(col => (
                          <th
                            key={col.field}
                            className="px-3 py-3 cursor-pointer select-none hover:text-brand-primary transition-colors text-xs"
                            onClick={() => handleSort(col.field)}
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              <SortIcon field={col.field} />
                            </span>
                          </th>
                        ))}
                        <th className="px-3 py-3 text-right text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-light/50">
                      {tbcJobs.map((job) => {
                        const CategoryIcon = getCategoryIcon(job.job_category);
                        return (
                          <tr key={job.job_uid} className="hover:bg-brand-light/20 transition-all duration-150 group">
                            <td className="px-3 py-3 font-mono text-xs text-brand-neutral">
                              <div className="inline-flex items-center gap-0.5 group/wo">
                                {job.work_order_number || 'N/A'}
                                <button
                                  onClick={(e) => openJobInZuper(e, job.job_uid)}
                                  className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                                  title="Open in Zuper"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                                {job.work_order_number && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(String(job.work_order_number)); setToast('Job number copied!'); setTimeout(() => setToast(null), 2000); }}
                                    className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                                    title="Copy job number"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="font-medium text-brand-dark truncate">{job.customer_name || 'Unknown'}</div>
                              {job.job_title && (
                                <div className="text-[11px] text-brand-neutral/70 truncate">{job.job_title}</div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-brand-neutral text-xs">
                              {job.asset_name ? (
                                <div className="truncate">
                                  <div className="truncate">{job.asset_code ? `${job.asset_code} - ` : ''}{job.asset_name}</div>
                                  {job.asset_serial_number && (
                                    <div className="font-mono text-[11px] text-brand-neutral/70 truncate">{job.asset_serial_number}</div>
                                  )}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[11px] font-medium text-white whitespace-nowrap ${getJobColor(job.job_category)}`}>
                                <CategoryIcon className="w-3 h-3" />
                                {job.job_category || 'N/A'}
                              </span>
                              <div className="flex items-center gap-1 mt-1 text-[10px] text-brand-neutral truncate">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{job.workshop_location || 'No Location'}</span>
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              {(() => {
                                const v = visitOf(job);
                                if (!v) return <span className="text-brand-neutral/40 text-[11px]">—</span>;
                                return (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-lg text-[11px] font-semibold bg-brand-light text-brand-primary"
                                    title={v.remaining > 0
                                      ? `${v.remaining} visit${v.remaining === 1 ? '' : 's'} left after this one`
                                      : 'Final visit of the contract'}
                                  >
                                    {v.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-3 text-brand-neutral whitespace-nowrap text-sm">
                              <div>{job.scheduled_date || 'Not scheduled'}</div>
                              {(() => {
                                const { slot, displayTime } = getScheduleDisplay(job);
                                return (displayTime || slot) ? (
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {displayTime && <span className="text-xs text-brand-neutral/70">{displayTime}</span>}
                                    {slot && (
                                      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${
                                        slot === 'Morning' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                                      }`}>{slot}</span>
                                    )}
                                  </div>
                                ) : null;
                              })()}
                            </td>
                            <td className="px-3 py-3 text-brand-neutral font-mono text-[11px]">
                              {job.customer_phone || 'No phone'}
                            </td>
                            <td className="px-3 py-3 text-right">
                              {statusFilter === 'tbc' ? (
                                <button
                                  onClick={() => copyBookingLink(job.customer_uid, job.job_uid)}
                                  className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-secondary font-medium px-2 py-1.5 rounded-lg hover:bg-brand-light transition-colors text-[11px] whitespace-nowrap"
                                >
                                  <Link className="w-3 h-3" /> Copy Link
                                </button>
                              ) : (
                                <button
                                  onClick={() => setDrawerJob(job)}
                                  className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-secondary font-medium px-2 py-1.5 rounded-lg hover:bg-brand-light transition-colors text-[11px] whitespace-nowrap"
                                >
                                  <Wrench className="w-3 h-3" /> Details
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Job Details Drawer */}
      {drawerJob && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30" onClick={() => setDrawerJob(null)} />
          {/* Drawer Panel */}
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-brand-light/60">
              <div>
                <h2 className="text-base font-bold text-brand-dark">{drawerJob.customer_name || 'Unknown'}</h2>
                <p className="text-xs text-brand-neutral mt-0.5">
                  #{drawerJob.work_order_number} · {drawerJob.job_category || 'N/A'}
                </p>
              </div>
              <button onClick={() => setDrawerJob(null)} className="p-2 rounded-xl hover:bg-brand-light transition-colors">
                <X className="w-5 h-5 text-brand-neutral" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {jobDetailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                </div>
              ) : jobDetails ? (
                <>
                  {/* Job Info */}
                  <div className="bg-brand-light/30 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-brand-dark uppercase tracking-wider">Job Info</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[11px] text-brand-neutral">Status</span>
                        <p className="font-medium text-brand-dark">{jobDetails.status || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[11px] text-brand-neutral">Category</span>
                        <p className="font-medium text-brand-dark">{jobDetails.category || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[11px] text-brand-neutral">Scheduled Date</span>
                        <p className="font-medium text-brand-dark">{jobDetails.scheduledDate || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[11px] text-brand-neutral">Scheduled Time</span>
                        <p className="font-medium text-brand-dark">{jobDetails.scheduledTime || '-'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {jobDetails.slotLabel && (
                        <div>
                          <span className="text-[11px] text-brand-neutral">Slot</span>
                          <p className="font-medium text-brand-dark">{jobDetails.slotLabel}{jobDetails.buggyPosition ? ` #${jobDetails.buggyPosition}` : ''}</p>
                        </div>
                      )}
                      {jobDetails.title && (
                        <div>
                          <span className="text-[11px] text-brand-neutral">Job Title</span>
                          <p className="font-medium text-brand-dark">{jobDetails.title}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Assigned To */}
                  <div className="bg-brand-light/30 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-brand-dark uppercase tracking-wider">Assigned To</h3>
                    {jobDetails.assignedTo?.length > 0 ? (
                      <div className="space-y-2">
                        {jobDetails.assignedTo.map((tech, i) => (
                          <div key={i} className="flex items-center gap-2.5 bg-white rounded-lg px-3 py-2 border border-brand-light/60">
                            <div className="w-7 h-7 rounded-full bg-brand-primary/10 flex items-center justify-center">
                              <User className="w-3.5 h-3.5 text-brand-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-brand-dark">{tech.name}</p>
                              {tech.email && <p className="text-[11px] text-brand-neutral">{tech.email}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-brand-neutral">No technician assigned</p>
                    )}
                  </div>

                  {/* Assets */}
                  {jobDetails.assets?.length > 0 && (
                    <div className="bg-brand-light/30 rounded-xl p-4 space-y-3">
                      <h3 className="text-xs font-semibold text-brand-dark uppercase tracking-wider">Assets</h3>
                      {jobDetails.assets.map((asset, i) => (
                        <div key={i} className="bg-white rounded-lg px-3 py-2 border border-brand-light/60 text-sm">
                          <p className="font-medium text-brand-dark">{asset.code ? `${asset.code} - ` : ''}{asset.name || 'Unknown'}</p>
                          {asset.serialNumber && <p className="text-[11px] text-brand-neutral font-mono">{asset.serialNumber}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Child Jobs */}
                  {jobDetails.childJobs?.length > 0 && (
                    <div className="bg-brand-light/30 rounded-xl p-4 space-y-3">
                      <h3 className="text-xs font-semibold text-brand-dark uppercase tracking-wider">
                        Child Jobs ({jobDetails.childJobs.length})
                      </h3>
                      <div className="space-y-2">
                        {jobDetails.childJobs.map((child, i) => (
                          <div key={i} className="bg-white rounded-lg px-3 py-3 border border-brand-light/60 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-brand-dark">
                                #{child.workOrderNumber || '—'}
                              </span>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-lg ${
                                child.status === 'Scheduled' ? 'bg-green-100 text-green-800'
                                  : child.status === 'TBC' ? 'bg-amber-100 text-amber-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>{child.status || 'Unknown'}</span>
                            </div>
                            {child.title && <p className="text-xs text-brand-neutral"><span className="text-brand-neutral/50 mr-1">Job:</span>{child.title}</p>}
                            {child.category && (
                              <p className="text-[11px] text-brand-neutral"><span className="text-brand-neutral/50 mr-1">Category:</span>{child.category}</p>
                            )}
                            <div className="flex items-center gap-3 text-[11px] text-brand-neutral">
                              {child.scheduledDate && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" /><span className="text-brand-neutral/50 mr-0.5">Date:</span> {child.scheduledDate}
                                </span>
                              )}
                              {child.scheduledTime && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" /><span className="text-brand-neutral/50 mr-0.5">Time:</span> {child.scheduledTime}
                                </span>
                              )}
                            </div>
                            {child.assignedTo?.length > 0 && (
                              <div className="flex items-center gap-1 text-[11px] text-brand-neutral">
                                <User className="w-3 h-3" />
                                <span className="text-brand-neutral/50 mr-0.5">Tech:</span>
                                {child.assignedTo.map(a => a.name).join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Open in Zuper */}
                  <button
                    onClick={() => window.open(`${zuperAppUrl}/jobs/${drawerJob.job_uid}/details`, '_blank')}
                    className="w-full flex items-center justify-center gap-2 text-brand-primary font-medium px-3 py-2.5 rounded-xl border border-brand-light hover:bg-brand-light transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" /> Open in Zuper
                  </button>
                </>
              ) : (
                <p className="text-sm text-brand-neutral text-center py-8">Failed to load job details</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-brand-dark text-white px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium animate-fade-in">
          <Check className="w-4 h-4 text-green-400" />
          {toast}
        </div>
      )}
    </Layout>
  );
}
